import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE, isNotFoundError } from '@/utils/apiError';
import {
  getPatientAddressList,
  getPatientAddress,
  createPatientAddress,
  updatePatientAddress,
  deletePatientAddress,
} from '@/services/main/patientAddresses';
import type { PatientAddressFormData } from '@/schemas/patientAddressSchema';

/** List patient addresses for a patient */
export const usePatientAddressListQuery = (patientId: number | null) => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery({
    queryKey: ['patient-addresses', 'list', tenant, clinicNumber, patientId, serviceType],
    queryFn: () => getPatientAddressList(tenant, clinicNumber, patientId!),
    enabled: !!tenant && !!clinicNumber && !!patientId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data: data ?? [], isLoading, isError, isPending, refetch, error };
};

/** Single patient address (for edit form) */
export const usePatientAddressQuery = (patientId: number | null, addressId: number | null) => {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery({
    queryKey: ['patient-addresses', 'detail', tenant, clinicNumber, patientId, addressId],
    queryFn: () => getPatientAddress(tenant, clinicNumber, patientId!, addressId!),
    enabled: !!tenant && !!clinicNumber && !!patientId && !!addressId,
    meta: { showNotFoundToast: true },
  });

  useEffect(() => {
    if (isError && error && !isNotFoundError(error)) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, refetch, error };
};

/** Create patient address */
export const usePatientAddressCreateMutation = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync: createMutation, isPending: isLoading } = useMutation({
    mutationFn: ({ patientId, data }: { patientId: number; data: PatientAddressFormData }) =>
      createPatientAddress(tenant, clinicNumber, patientId, data),
    onSuccess: (_, { patientId }) => {
      queryClient.invalidateQueries({ queryKey: ['patient-addresses', 'list', tenant, clinicNumber, patientId] });
      toast.success('更新が正常に終了しました。');
    },
    onError: err => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) return;
      toast.error(getApiErrorMessage(err, '更新に失敗しました。'));
    },
  });

  return { createMutation, isLoading };
};

/** Update patient address */
export const usePatientAddressUpdateMutation = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync: updateMutation, isPending: isLoading } = useMutation({
    mutationFn: ({
      patientId,
      addressId,
      data,
    }: {
      patientId: number;
      addressId: number;
      data: PatientAddressFormData;
    }) => updatePatientAddress(tenant, clinicNumber, patientId, addressId, data),
    onSuccess: (_, { patientId, addressId }) => {
      queryClient.invalidateQueries({
        queryKey: ['patient-addresses', 'list', tenant, clinicNumber, patientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['patient-addresses', 'detail', tenant, clinicNumber, patientId, addressId],
      });
      toast.success('更新が正常に終了しました。');
    },
    onError: err => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 404) return;
      toast.error(getApiErrorMessage(err, '更新に失敗しました。'));
    },
  });

  return { updateMutation, isLoading };
};

/** Delete patient address */
export const usePatientAddressDeleteMutation = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync: deleteMutation, isPending: isLoading } = useMutation({
    mutationFn: ({ patientId, addressId }: { patientId: number; addressId: number }) =>
      deletePatientAddress(tenant, clinicNumber, patientId, addressId),
    onSuccess: (_, { patientId }) => {
      queryClient.invalidateQueries({
        queryKey: ['patient-addresses', 'list', tenant, clinicNumber, patientId],
      });
      toast.success('削除が正常に終了しました。');
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, '削除ができませんでした。'));
    },
  });

  return { deleteMutation, isLoading };
};
